import { useState } from 'react'
import { AlertTriangle, Download, Plus } from 'lucide-react'
import {
  CurrencyValue, EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { usePayrollRuns } from '@/features/payroll'
import { toApiProblem } from '@/lib/api/client'
import {
  usePaymentBatchLines, usePaymentBatches, usePaymentMutations, useWpsLayouts,
} from '../api'
import { BATCH_STATUS_LABELS } from '../types'
import type { BatchStatus } from '../types'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

const STATUS_TONE: Record<BatchStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  DRAFT: 'neutral',
  GENERATED: 'info',
  SENT: 'warning',
  CONFIRMED: 'success',
  FAILED: 'danger',
  CANCELLED: 'neutral',
}

/**
 * Payment batches, and — the part that matters — who is not in one.
 *
 * A batch always contains a line for everybody the run calculated. People it cannot pay are
 * shown with the reason rather than left out, because a file that quietly pays fewer people
 * than the run reconciles against nothing and the person dropped finds out on payday.
 */
export function PaymentBatchesPage() {
  useBreadcrumb()
  const batches = usePaymentBatches()
  const layouts = useWpsLayouts()
  const runs = usePayrollRuns()
  const { prepare, generate, markSent, confirm } = usePaymentMutations()

  const [selected, setSelected] = useState<string | null>(null)
  const lines = usePaymentBatchLines(selected)

  const [draft, setDraft] = useState<{
    payrollRunId: string; wpsLayoutId: string; valueDate: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const approvedRuns = runs.data?.filter(
    (run) => run.status === 'APPROVED' || run.status === 'PAID') ?? []
  const chosen = batches.data?.find((batch) => batch.id === selected)

  const act = (work: () => Promise<unknown>, fallback: string) => {
    void (async () => {
      setError(null)
      try {
        await work()
      } catch (caught) {
        setError(toApiProblem(caught)?.detail ?? fallback)
      }
    })()
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Payments"
        subtitle="What is being paid, into which accounts, and who could not be included."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                payrollRunId: approvedRuns[0]?.id ?? '',
                wpsLayoutId: layouts.data?.[0]?.id ?? '',
                valueDate: '',
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New batch
          </Button>
        }
      />

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      <Card>
        {batches.isPending && <Skeleton className="m-4 h-24" />}
        {batches.isError && (
          <ErrorState error={batches.error} onRetry={() => void batches.refetch()} />
        )}
        {batches.isSuccess && batches.data.length === 0 && (
          <EmptyState
            title="Nothing has been paid"
            description="A batch is built from an approved run, generated into a file, uploaded to the bank and then confirmed."
          />
        )}
        {batches.isSuccess && batches.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Reference</th>
                  <th className="p-3 text-start font-medium">Period</th>
                  <th className="p-3 text-end font-medium">Paid</th>
                  <th className="p-3 text-end font-medium">People</th>
                  <th className="p-3 text-end font-medium">Not paid</th>
                  <th className="p-3 text-start font-medium">Status</th>
                  <th className="p-3 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {batches.data.map((batch) => (
                  <tr key={batch.id} className="border-b border-slate-100">
                    <td className="p-3">
                      <button
                        type="button"
                        className="font-mono underline underline-offset-2"
                        onClick={() => setSelected(batch.id)}
                      >
                        {batch.reference}
                      </button>
                    </td>
                    <td className="p-3 text-slate-600">
                      {batch.periodStart} → {batch.periodEnd}
                    </td>
                    <td className="p-3 text-end font-medium">
                      <CurrencyValue value={batch.totalAmount} currency={batch.currency} />
                    </td>
                    <td className="p-3 text-end">{batch.lineCount}</td>
                    <td className="p-3 text-end">
                      {batch.excludedCount > 0 ? (
                        <span className="inline-flex items-center gap-1 text-amber-700">
                          <AlertTriangle className="size-3.5" aria-hidden="true" />
                          {batch.excludedCount}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="p-3">
                      <StatusBadge
                        status={batch.status}
                        tone={STATUS_TONE[batch.status as BatchStatus] ?? 'neutral'}
                        label={BATCH_STATUS_LABELS[batch.status as BatchStatus] ?? batch.status}
                      />
                    </td>
                    <td className="p-3 text-end">
                      <div className="flex justify-end gap-2">
                        {batch.status === 'DRAFT' && (
                          <Button
                            type="button"
                            onClick={() => act(
                              () => generate.mutateAsync(batch.id),
                              'That file could not be generated')}
                          >
                            Generate file
                          </Button>
                        )}
                        {batch.status === 'GENERATED' && (
                          <>
                            {batch.generatedFileId && (
                              <Button asChild variant="outline">
                                <a href={`/api/v1/files/${batch.generatedFileId}/content`}>
                                  <Download className="size-4" aria-hidden="true" />
                                  Download
                                </a>
                              </Button>
                            )}
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => act(
                                () => markSent.mutateAsync({ id: batch.id }),
                                'That batch could not be marked as sent')}
                            >
                              Mark uploaded
                            </Button>
                          </>
                        )}
                        {batch.status === 'SENT' && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => act(
                              () => confirm.mutateAsync(batch.id),
                              'That batch could not be confirmed')}
                          >
                            Bank confirmed
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {chosen && (
        <>
          <PageHeader
            title={`Lines of ${chosen.reference}`}
            subtitle="Everybody the run calculated, whether or not the file can pay them."
          />
          <Card>
            {lines.isPending && <Skeleton className="m-4 h-20" />}
            {lines.isSuccess && (
              <div className="overflow-x-auto overscroll-x-contain">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 text-slate-600">
                    <tr>
                      <th className="p-3 text-end font-medium">#</th>
                      <th className="p-3 text-start font-medium">Person</th>
                      <th className="p-3 text-start font-medium">Account</th>
                      <th className="p-3 text-end font-medium">Amount</th>
                      <th className="p-3 text-start font-medium">In the file</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.data.map((line) => (
                      <tr key={line.id} className="border-b border-slate-100 align-top">
                        <td className="p-3 text-end text-slate-600">{line.lineNumber}</td>
                        <td className="p-3">
                          {line.employeeName ?? line.employeeCode ?? '—'}
                        </td>
                        <td className="p-3 font-mono text-xs text-slate-600">
                          {line.iban ?? '—'}
                        </td>
                        <td className="p-3 text-end">
                          <CurrencyValue value={line.amount} currency={line.currency} />
                        </td>
                        <td className="p-3">
                          {line.excludedReason ? (
                            <span className="text-amber-700">{line.excludedReason}</span>
                          ) : (
                            <span className="text-slate-600">Yes</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) setDraft(null) }}
        title="Build a payment batch"
        description="From an approved run. The file is written in a second step, after you have looked at the lines."
        submitLabel="Build batch"
        loading={prepare.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              await prepare.mutateAsync({
                payrollRunId: draft.payrollRunId,
                wpsLayoutId: draft.wpsLayoutId || null,
                valueDate: draft.valueDate || null,
              })
              setDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That batch could not be built')
            }
          })()
        }}
      >
        {draft && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="batch-run">Payroll run</Label>
              <Select
                id="batch-run"
                className="w-full"
                value={draft.payrollRunId}
                onChange={(event) => setDraft({ ...draft, payrollRunId: event.target.value })}
              >
                <option value="">Choose a run…</option>
                {approvedRuns.map((run) => (
                  <option key={run.id} value={run.id}>
                    {run.periodStart} → {run.periodEnd} ({run.status})
                  </option>
                ))}
              </Select>
              {approvedRuns.length === 0 && (
                <p className="mt-1 text-xs text-amber-700">
                  No run is approved. Paying from unapproved figures is what approval exists
                  to prevent, so there is nothing to build from yet.
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="batch-layout">WPS layout</Label>
              <Select
                id="batch-layout"
                className="w-full"
                value={draft.wpsLayoutId}
                onChange={(event) => setDraft({ ...draft, wpsLayoutId: event.target.value })}
              >
                <option value="">None — build the lines only</option>
                {layouts.data?.map((layout) => (
                  <option key={layout.id} value={layout.id}>
                    {layout.name}
                    {layout.verifiedAgainstBankSpec ? '' : ' (not checked against the bank)'}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="batch-value-date">Value date</Label>
              <DatePicker
                id="batch-value-date" value={draft.valueDate}
                onChange={(event) => setDraft({ ...draft, valueDate: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
